import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User } from '../types/auth';
import { getStoredToken, removeStoredToken, setStoredToken, setStoredUser } from '../utils/storage';
import { authApi } from '../services/authApi';
import { setAuthToken, clearAuthToken } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESTORE_SESSION'; payload: { user: User; token: string } };

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    default:
      return state;
  }
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 恢复会话
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = getStoredToken();
        if (storedToken) {
          // 设置axios认证头部
          setAuthToken(storedToken);
          // 验证token并获取用户信息
          const userInfo = await authApi.getCurrentUser(storedToken);
          dispatch({
            type: 'RESTORE_SESSION',
            payload: { user: userInfo, token: storedToken },
          });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        // Token无效，清除存储和axios头部
        removeStoredToken();
        clearAuthToken();
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    restoreSession();
  }, []);

  // 监听全局认证登出事件
  useEffect(() => {
    const handleAuthLogout = () => {
      clearAuthToken();
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await authApi.login(email, password);
      
      // 存储token和用户信息，并设置axios默认头部
      setStoredToken(response.access_token);
      setStoredUser(response.user);
      setAuthToken(response.access_token);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          token: response.access_token,
        },
      });
      
      return { success: true };
    } catch (error: any) {
      dispatch({ type: 'LOGIN_FAILURE' });
      return {
        success: false,
        error: error.response?.data?.detail || '登录失败，请重试',
      };
    }
  };

  const logout = () => {
    removeStoredToken();
    clearAuthToken();
    dispatch({ type: 'LOGOUT' });
  };

  const refreshUser = async () => {
    if (state.token) {
      try {
        const userInfo = await authApi.getCurrentUser(state.token);
        dispatch({
          type: 'RESTORE_SESSION',
          payload: { user: userInfo, token: state.token },
        });
      } catch (error) {
        // Token无效，自动登出
        logout();
      }
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}